// Admin — Jornadas de implantação (Onda 1)
// Reutiliza o schema atual (journey_templates/stages/missions + mission_slides
// /questions/deliverables/external_tasks/resources_contextual) e alimenta
// exatamente a mesma estrutura consumida pelo workspace do membro.
//
// Segurança: todas as funções exigem sessão autenticada + role admin.
// Nenhum item já usado por membros é apagado sem confirmação explícita.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdminLevel } from "@/lib/rbac/server";

// SDE.4 · Onda 4.4 — Toda a superfície de admin de jornadas (leitura +
// escrita) manipula conteúdo publicável. Readonly não deve ver o editor;
// editor e full podem. Enforcement duplo: has_role('admin') (legado
// inclusivo) + nível mínimo `editor` quando rbac_enforced=true.
async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (!data) throw new Error("Forbidden");
  await assertAdminLevel(ctx as never, "editor");
}

// Campos permitidos no business_workspace (mesma lista de workspace.functions.ts)
export const WORKSPACE_FIELDS = [
  "model", "audience", "niche", "offer", "products",
  "pricing", "brand", "channels", "sales_plan",
  "prospects", "proposals", "extras",
] as const;

const WORKSPACE_FIELD_SET = new Set(WORKSPACE_FIELDS);

// ============ LISTAGEM ============

export const listBusinessJourneys = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { filter?: "active" | "archived" | "all" } = {}) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;

    const [{ data: businesses }, { data: templates }] = await Promise.all([
      s.from("businesses")
        .select("id, slug, title, publication_status, active, categories(name)")
        .order("title"),
      s.from("journey_templates")
        .select("id, business_id, title, published, is_default, updated_at, estimated_days, archived_at"),
    ]);

    const filter = data?.filter ?? "active";
    const templatesFiltered = (templates ?? []).filter((t: any) => {
      if (filter === "active") return !t.archived_at;
      if (filter === "archived") return !!t.archived_at;
      return true;
    });

    const templatesByBiz = new Map<string, any>();
    const baseTemplates: any[] = [];
    for (const t of templatesFiltered) {
      if (t.business_id) templatesByBiz.set(t.business_id, t);
      else baseTemplates.push(t);
    }

    // Missão-count por template (uma query agregada)
    const templateIds = templatesFiltered.map((t: any) => t.id);
    const stageIds: string[] = [];
    const missionCountByTemplate = new Map<string, number>();
    if (templateIds.length) {
      const { data: stages } = await s
        .from("journey_stages")
        .select("id, template_id")
        .in("template_id", templateIds)
        .is("deleted_at", null);
      const stageToTpl = new Map<string, string>();
      for (const st of stages ?? []) {
        stageIds.push(st.id);
        stageToTpl.set(st.id, st.template_id);
      }
      if (stageIds.length) {
        const { data: missions } = await s
          .from("journey_missions")
          .select("id, stage_id")
          .in("stage_id", stageIds)
          .is("deleted_at", null);
        for (const m of missions ?? []) {
          const tpl = stageToTpl.get(m.stage_id);
          if (!tpl) continue;
          missionCountByTemplate.set(tpl, (missionCountByTemplate.get(tpl) ?? 0) + 1);
        }
      }
    }

    const mapTpl = (t: any) => ({
      id: t.id,
      title: t.title,
      published: t.published,
      updated_at: t.updated_at,
      archived_at: t.archived_at,
      is_default: t.is_default,
      mission_count: missionCountByTemplate.get(t.id) ?? 0,
    });

    return {
      businesses: (businesses ?? []).map((b: any) => {
        const t = templatesByBiz.get(b.id);
        return {
          id: b.id,
          slug: b.slug,
          title: b.title,
          category: b.categories?.name ?? null,
          publication_status: b.publication_status,
          active: b.active,
          template: t ? mapTpl(t) : null,
        };
      }),
      baseTemplates: baseTemplates.map(mapTpl),
      filter,
    };
  });


// ============ TEMPLATE ============

export const ensureBusinessTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { businessId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const { data: existing } = await s
      .from("journey_templates")
      .select("id")
      .eq("business_id", data.businessId)
      .maybeSingle();
    if (existing) return { id: existing.id, created: false };
    const { data: biz } = await s
      .from("businesses").select("title").eq("id", data.businessId).maybeSingle();
    const { data: created, error } = await s
      .from("journey_templates")
      .insert({
        business_id: data.businessId,
        title: `Jornada — ${biz?.title ?? "Negócio"}`,
        published: false,
        is_default: false,
      })
      .select("id").single();
    if (error) throw new Error(error.message);
    return { id: created.id, created: true };
  });

export const getTemplateTree = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const { data: template } = await s
      .from("journey_templates")
      .select("id, business_id, title, description, published, is_default, estimated_days, updated_at")
      .eq("id", data.templateId).maybeSingle();
    if (!template) throw new Error("Jornada não encontrada.");

    const { data: business } = template.business_id
      ? await s.from("businesses").select("id, slug, title").eq("id", template.business_id).maybeSingle()
      : { data: null } as any;

    const { data: stages } = await s
      .from("journey_stages")
      .select("id, title, objective, icon, order_index")
      .eq("template_id", data.templateId)
      .is("deleted_at", null)
      .order("order_index");

    const stageIds = (stages ?? []).map((x: any) => x.id);
    const { data: missions } = stageIds.length
      ? await s.from("journey_missions")
          .select("id, stage_id, title, objective, description, order_index, estimated_minutes, difficulty, depends_on_mission_id")
          .in("stage_id", stageIds)
          .is("deleted_at", null)
          .order("order_index")
      : { data: [] as any[] };

    const missionIds = (missions ?? []).map((m: any) => m.id);
    const [slidesRes, questionsRes, delivRes, extRes, resRes] = missionIds.length ? await Promise.all([
      s.from("mission_slides").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
      s.from("mission_questions").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
      s.from("mission_deliverables").select("mission_id, target_field, kind").in("mission_id", missionIds).is("deleted_at", null),
      s.from("mission_external_tasks").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
      s.from("mission_resources_contextual").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
    ]) : [{ data: [] }, { data: [] }, { data: [] }, { data: [] }, { data: [] }];


    const count = (arr: any[], id: string) => arr.filter(x => x.mission_id === id).length;
    const enrichedMissions = (missions ?? []).map((m: any) => ({
      ...m,
      _counts: {
        slides: count(slidesRes.data ?? [], m.id),
        questions: count(questionsRes.data ?? [], m.id),
        externals: count(extRes.data ?? [], m.id),
        resources: count(resRes.data ?? [], m.id),
      },
      _deliverable: (delivRes.data ?? []).find((d: any) => d.mission_id === m.id) ?? null,
    }));

    const enrichedStages = (stages ?? []).map((st: any) => ({
      ...st,
      missions: enrichedMissions.filter((m: any) => m.stage_id === st.id),
    }));

    return { template, business, stages: enrichedStages };
  });

export const updateTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id: string;
    title?: string;
    description?: string | null;
    estimated_days?: number | null;
  }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { id, ...patch } = data;
    const { error } = await context.supabase.from("journey_templates").update(patch).eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const setTemplatePublished = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; published: boolean }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase
      .from("journey_templates").update({ published: data.published }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============ STAGES ============

export const upsertStage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id?: string;
    template_id: string;
    title: string;
    objective?: string | null;
    icon?: string | null;
  }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    if (data.id) {
      const { error } = await s.from("journey_stages")
        .update({ title: data.title, objective: data.objective ?? null, icon: data.icon ?? null })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: max } = await s.from("journey_stages")
      .select("order_index").eq("template_id", data.template_id)
      .order("order_index", { ascending: false }).limit(1).maybeSingle();
    const next = (max?.order_index ?? -1) + 1;
    const { data: created, error } = await s.from("journey_stages")
      .insert({
        template_id: data.template_id, title: data.title,
        objective: data.objective ?? null, icon: data.icon ?? null,
        order_index: next,
      }).select("id").single();
    if (error) throw new Error(error.message);
    return { id: created.id };
  });

// Arquivamento suave (preserva IDs; snapshots históricos permanecem intactos;
// itens arquivados são ignorados em novas publicações por build_template_snapshot).
export const deleteStage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("journey_stages")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveStage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "journey_stages", "template_id", data.id, data.direction);
    return { ok: true };
  });

// ============ MISSIONS ============

export const upsertMission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id?: string;
    stage_id: string;
    title: string;
    objective?: string | null;
    description?: string | null;
    expected_result?: string | null;
    estimated_minutes?: number | null;
    difficulty?: string | null;
    depends_on_mission_id?: string | null;
  }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const patch: any = {
      title: data.title,
      objective: data.objective ?? null,
      description: data.description ?? null,
      expected_result: data.expected_result ?? null,
      estimated_minutes: data.estimated_minutes ?? null,
      difficulty: data.difficulty ?? null,
      depends_on_mission_id: data.depends_on_mission_id ?? null,
    };
    if (data.id) {
      const { error } = await s.from("journey_missions").update(patch).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: max } = await s.from("journey_missions")
      .select("order_index").eq("stage_id", data.stage_id)
      .order("order_index", { ascending: false }).limit(1).maybeSingle();
    const { data: created, error } = await s.from("journey_missions")
      .insert({ ...patch, stage_id: data.stage_id, order_index: (max?.order_index ?? -1) + 1 })
      .select("id").single();
    if (error) throw new Error(error.message);
    return { id: created.id };
  });

export const deleteMission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("journey_missions")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveMission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "journey_missions", "stage_id", data.id, data.direction);
    return { ok: true };
  });

// ============ MISSION EDITOR (full read) ============

export const getMissionEditor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { missionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const [{ data: mission }, slidesR, questionsR, delivR, extR, resR, stateCountR, progressCountR] = await Promise.all([
      s.from("journey_missions").select("*").eq("id", data.missionId).maybeSingle(),
      s.from("mission_slides").select("*").eq("mission_id", data.missionId).is("deleted_at", null).order("order_index"),
      s.from("mission_questions").select("*").eq("mission_id", data.missionId).is("deleted_at", null).order("order_index"),
      s.from("mission_deliverables").select("*").eq("mission_id", data.missionId).is("deleted_at", null).maybeSingle(),
      s.from("mission_external_tasks").select("*").eq("mission_id", data.missionId).is("deleted_at", null).order("order_index"),
      s.from("mission_resources_contextual").select("*").eq("mission_id", data.missionId).is("deleted_at", null).order("order_index"),
      s.from("user_mission_state").select("id", { count: "exact", head: true }).eq("mission_id", data.missionId),
      s.from("user_mission_progress").select("id", { count: "exact", head: true }).eq("mission_id", data.missionId),
    ]);

    if (!mission) throw new Error("Missão não encontrada.");

    // Sibling missions for depends_on selector
    const { data: siblings } = await s.from("journey_missions")
      .select("id, title, stage_id, order_index")
      .neq("id", data.missionId)
      .order("order_index");

    return {
      mission,
      slides: slidesR.data ?? [],
      questions: questionsR.data ?? [],
      deliverable: delivR.data ?? null,
      externals: extR.data ?? [],
      resources: resR.data ?? [],
      usage: {
        states: stateCountR.count ?? 0,
        progress: progressCountR.count ?? 0,
      },
      siblings: siblings ?? [],
    };
  });

// ============ SLIDES ============

const slideSchema = z.object({
  id: z.string().uuid().optional(),
  mission_id: z.string().uuid(),
  title: z.string().min(1),
  body: z.string().nullish(),
  example: z.string().nullish(),
  icon: z.string().nullish(),
  image_url: z.string().url().nullish().or(z.literal("").transform(() => null)),
});

export const upsertSlide = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => slideSchema.parse(raw))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return upsertOrdered(context.supabase, "mission_slides", "mission_id", data);
  });

export const deleteSlide = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("mission_slides")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveSlide = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "mission_slides", "mission_id", data.id, data.direction);
    return { ok: true };
  });

// ============ QUESTIONS ============

const questionSchema = z.object({
  id: z.string().uuid().optional(),
  mission_id: z.string().uuid(),
  key: z.string().min(1).regex(/^[a-z0-9_]+$/i, "Use apenas letras, números e _"),
  prompt: z.string().min(1),
  help_text: z.string().nullish(),
  input_type: z.enum(["single", "multi", "text", "textarea", "number"]),
  options: z.any().optional(),
  required: z.boolean().default(true),
  allow_other: z.boolean().default(false),
  condition: z.any().nullish(),
});

export const upsertQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => questionSchema.parse(raw))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    // Enforce unique key within mission
    const { data: dupe } = await context.supabase
      .from("mission_questions")
      .select("id").eq("mission_id", data.mission_id).eq("key", data.key)
      .neq("id", data.id ?? "00000000-0000-0000-0000-000000000000")
      .maybeSingle();
    if (dupe) throw new Error(`Chave "${data.key}" já usada nesta missão.`);
    return upsertOrdered(context.supabase, "mission_questions", "mission_id", data);
  });

export const deleteQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("mission_questions")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "mission_questions", "mission_id", data.id, data.direction);
    return { ok: true };
  });

// ============ DELIVERABLE ============

const deliverableSchema = z.object({
  id: z.string().uuid().optional(),
  mission_id: z.string().uuid(),
  kind: z.string().min(1).default("configuration"),
  title: z.string().min(1),
  description: z.string().nullish(),
  target_field: z.string().refine((v) => WORKSPACE_FIELD_SET.has(v as any), "Destino inválido"),
  template: z.any().optional(),
});

export const upsertDeliverable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => deliverableSchema.parse(raw))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const payload: any = {
      kind: data.kind ?? "configuration",
      title: data.title,
      description: data.description ?? null,
      target_field: data.target_field,
      template: data.template ?? {},
    };
    if (data.id) {
      const { error } = await s.from("mission_deliverables").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await s.from("mission_deliverables")
      .select("id").eq("mission_id", data.mission_id).maybeSingle();
    if (existing) {
      const { error } = await s.from("mission_deliverables").update(payload).eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { id: existing.id };
    }
    const { data: created, error } = await s.from("mission_deliverables")
      .insert({ ...payload, mission_id: data.mission_id }).select("id").single();
    if (error) throw new Error(error.message);
    return { id: created.id };
  });

export const deleteDeliverable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("mission_deliverables")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


// ============ EXTERNAL TASKS ============

const externalSchema = z.object({
  id: z.string().uuid().optional(),
  mission_id: z.string().uuid(),
  key: z.string().min(1).regex(/^[a-z0-9_]+$/i, "Use apenas letras, números e _"),
  title: z.string().min(1),
  description: z.string().nullish(),
  url: z.string().url().nullish().or(z.literal("").transform(() => null)),
});

export const upsertExternalTask = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => externalSchema.parse(raw))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { data: dupe } = await context.supabase
      .from("mission_external_tasks")
      .select("id").eq("mission_id", data.mission_id).eq("key", data.key)
      .neq("id", data.id ?? "00000000-0000-0000-0000-000000000000")
      .maybeSingle();
    if (dupe) throw new Error(`Chave "${data.key}" já usada nesta missão.`);
    return upsertOrdered(context.supabase, "mission_external_tasks", "mission_id", data);
  });

export const deleteExternalTask = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("mission_external_tasks")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveExternalTask = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "mission_external_tasks", "mission_id", data.id, data.direction);
    return { ok: true };
  });

// ============ RESOURCES ============

const resourceSchema = z.object({
  id: z.string().uuid().optional(),
  mission_id: z.string().uuid(),
  anchor_type: z.enum(["mission", "slide", "question", "step"]),
  anchor_id: z.string().uuid().nullish(),
  anchor_key: z.string().nullish(),
  title: z.string().min(1),
  url: z.string().url().nullish().or(z.literal("").transform(() => null)),
  kind: z.string().min(1),
  description: z.string().nullish(),
});

export const upsertResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => resourceSchema.parse(raw))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return upsertOrdered(context.supabase, "mission_resources_contextual", "mission_id", data);
  });

export const deleteResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("mission_resources_contextual")
      .update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, archived: true };
  });


export const moveResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; direction: "up" | "down" }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    await swapOrder(context.supabase, "mission_resources_contextual", "mission_id", data.id, data.direction);
    return { ok: true };
  });

// ============ VALIDAÇÃO ============

export type ValidationIssue = {
  severity: "error" | "warning";
  code: string;
  message: string;
  ref?: string;
};

export const validateMission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { missionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const [{ data: mission }, { data: slides }, { data: questions }, { data: deliverable }, { data: externals }] =
      await Promise.all([
        s.from("journey_missions").select("*").eq("id", data.missionId).maybeSingle(),
        s.from("mission_slides").select("*").eq("mission_id", data.missionId).order("order_index"),
        s.from("mission_questions").select("*").eq("mission_id", data.missionId).order("order_index"),
        s.from("mission_deliverables").select("*").eq("mission_id", data.missionId).maybeSingle(),
        s.from("mission_external_tasks").select("*").eq("mission_id", data.missionId).order("order_index"),
      ]);

    const issues: ValidationIssue[] = [];
    if (!mission) return { issues: [{ severity: "error", code: "not_found", message: "Missão não encontrada." } as ValidationIssue] };
    if (!mission.title?.trim()) issues.push({ severity: "error", code: "no_title", message: "Missão sem título." });
    if (!mission.objective?.trim() && !mission.description?.trim())
      issues.push({ severity: "warning", code: "no_objective", message: "Missão sem objetivo/descrição." });
    if (mission.depends_on_mission_id) {
      const { data: dep } = await s.from("journey_missions")
        .select("id").eq("id", mission.depends_on_mission_id).maybeSingle();
      if (!dep) issues.push({ severity: "error", code: "bad_dependency", message: "Próxima/dependência aponta para missão inexistente." });
    }
    if ((slides ?? []).length === 0)
      issues.push({ severity: "warning", code: "no_slides", message: "Workspace sem slides — o membro pulará direto para Decidir." });

    const keys = new Set<string>();
    for (const q of questions ?? []) {
      if (keys.has(q.key)) {
        issues.push({ severity: "error", code: "dup_question_key", message: `Chave duplicada: ${q.key}`, ref: q.id });
      }
      keys.add(q.key);
      const needsOptions = q.input_type === "single" || q.input_type === "multi";
      const opts = Array.isArray(q.options) ? q.options : [];
      if (q.required && needsOptions && opts.length === 0)
        issues.push({ severity: "error", code: "no_options", message: `Pergunta obrigatória "${q.key}" sem opções.`, ref: q.id });
      if (q.condition) {
        try {
          const c: any = q.condition;
          if (c && c.when && !keys.has(c.when) && !((questions ?? []).find((x: any) => x.key === c.when)))
            issues.push({ severity: "error", code: "bad_condition", message: `Condicional em "${q.key}" referencia pergunta inexistente "${c.when}".`, ref: q.id });
        } catch {
          issues.push({ severity: "error", code: "bad_condition", message: `Condicional inválida em "${q.key}".`, ref: q.id });
        }
      }
    }

    if (!deliverable) {
      issues.push({ severity: "warning", code: "no_deliverable", message: "Missão sem entregável — nada será salvo em Meu Negócio." });
    } else {
      if (!deliverable.target_field) {
        issues.push({ severity: "error", code: "no_target", message: "Entregável sem destino." });
      } else if (!WORKSPACE_FIELD_SET.has(deliverable.target_field as any)) {
        issues.push({ severity: "error", code: "bad_target", message: `Destino "${deliverable.target_field}" não existe em Meu Negócio.` });
      }
    }

    const extKeys = new Set<string>();
    for (const e of externals ?? []) {
      if (!e.title?.trim()) issues.push({ severity: "error", code: "ext_no_title", message: "Checklist externo sem título.", ref: e.id });
      if (extKeys.has(e.key)) issues.push({ severity: "error", code: "ext_dup_key", message: `Chave duplicada no checklist externo: ${e.key}`, ref: e.id });
      extKeys.add(e.key);
    }

    return { issues };
  });

// ============ REORDER (drag-and-drop) ============
// Reorder in one shot given the full ordered list of ids for a parent.
// Uses a two-phase update (negative sentinel then final positions) to survive
// unique(parent, order_index) constraints if present.

const REORDER_MAP: Record<string, { table: string; parent: string }> = {
  stages: { table: "journey_stages", parent: "template_id" },
  missions: { table: "journey_missions", parent: "stage_id" },
  slides: { table: "mission_slides", parent: "mission_id" },
  questions: { table: "mission_questions", parent: "mission_id" },
  externals: { table: "mission_external_tasks", parent: "mission_id" },
  resources: { table: "mission_resources_contextual", parent: "mission_id" },
};

export const reorderEntities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    kind: "stages" | "missions" | "slides" | "questions" | "externals" | "resources";
    parentId: string;
    orderedIds: string[];
  }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const cfg = REORDER_MAP[data.kind];
    if (!cfg) throw new Error("Tipo inválido.");
    const s: any = context.supabase;

    // Load current siblings (non-archived) to validate the id set matches.
    const { data: siblings, error: sErr } = await s
      .from(cfg.table)
      .select("id")
      .eq(cfg.parent, data.parentId)
      .is("deleted_at", null);
    if (sErr) throw new Error(sErr.message);
    const sibIds = new Set((siblings ?? []).map((r: any) => r.id));
    if (data.orderedIds.length !== sibIds.size ||
        !data.orderedIds.every((id) => sibIds.has(id))) {
      throw new Error("Lista de ordenação inconsistente. Recarregue.");
    }

    // Phase 1: park into negative sentinels
    for (let i = 0; i < data.orderedIds.length; i++) {
      const { error } = await s.from(cfg.table)
        .update({ order_index: -1000 - i }).eq("id", data.orderedIds[i]);
      if (error) throw new Error(error.message);
    }
    // Phase 2: assign final positions
    for (let i = 0; i < data.orderedIds.length; i++) {
      const { error } = await s.from(cfg.table)
        .update({ order_index: i }).eq("id", data.orderedIds[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// ============ HELPERS ============

async function swapOrder(
  supabase: any,
  table: string,
  parentField: string,
  id: string,
  direction: "up" | "down",
) {
  const { data: current } = await supabase.from(table)
    .select(`id, order_index, ${parentField}`).eq("id", id).maybeSingle();
  if (!current) return;
  const op = direction === "up" ? "lt" : "gt";
  const order = direction === "up" ? { ascending: false } : { ascending: true };
  const { data: neighbor } = await supabase.from(table)
    .select("id, order_index")
    .eq(parentField, (current as any)[parentField])
    [op]("order_index", current.order_index)
    .order("order_index", order)
    .limit(1).maybeSingle();
  if (!neighbor) return;
  // Two-step swap using a temp negative value to avoid unique conflicts if any.
  await supabase.from(table).update({ order_index: -1 - Math.abs(current.order_index) }).eq("id", current.id);
  await supabase.from(table).update({ order_index: current.order_index }).eq("id", neighbor.id);
  await supabase.from(table).update({ order_index: neighbor.order_index }).eq("id", current.id);
}

async function upsertOrdered(
  supabase: any,
  table: string,
  parentField: string,
  data: any,
) {
  const { id, ...rest } = data;
  // Strip undefined so we don't overwrite columns with null accidentally.
  const patch: any = {};
  for (const [k, v] of Object.entries(rest)) if (v !== undefined) patch[k] = v;
  if (id) {
    const { error } = await supabase.from(table).update(patch).eq("id", id);
    if (error) throw new Error(error.message);
    return { id };
  }
  const { data: max } = await supabase.from(table)
    .select("order_index").eq(parentField, patch[parentField])
    .order("order_index", { ascending: false }).limit(1).maybeSingle();
  patch.order_index = (max?.order_index ?? -1) + 1;
  const { data: created, error } = await supabase.from(table).insert(patch).select("id").single();
  if (error) throw new Error(error.message);
  return { id: created.id };
}

// ============================================================
// Onda 2.A — Versionamento (rascunho / publicado / arquivado)
// ============================================================
// As RPCs publish_template_draft / ensure_template_draft /
// restore_template_version / build_template_snapshot são SECURITY DEFINER
// e foram revogadas de anon/authenticated. São chamadas via supabaseAdmin
// somente após checagem de role admin (assertAdmin acima).

async function loadAdmin() {
  const mod = await import("@/integrations/supabase/client.server");
  return mod.supabaseAdmin;
}

export const getVersionHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const [{ data: tmpl }, { data: versions }, { data: log }] = await Promise.all([
      s.from("journey_templates")
        .select("id, title, current_version_id, draft_version_id, last_published_at, last_edited_by")
        .eq("id", data.templateId)
        .maybeSingle(),
      s.from("journey_versions")
        .select("id, version_number, status, change_summary, based_on_version_id, published_at, published_by, created_at, updated_at")
        .eq("template_id", data.templateId)
        .order("version_number", { ascending: false })
        .order("created_at", { ascending: false }),
      s.from("journey_publication_log")
        .select("id, version_id, action, actor_id, notes, created_at")
        .eq("template_id", data.templateId)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);

    // Count projects per version (only those pinned)
    const versionIds = (versions ?? []).map((v: any) => v.id);
    const counts: Record<string, { total: number; active: number; completed: number }> = {};
    if (versionIds.length > 0) {
      const { data: projs } = await s
        .from("user_projects")
        .select("template_version_id, status")
        .in("template_version_id", versionIds);
      for (const p of (projs ?? []) as any[]) {
        const k = p.template_version_id;
        counts[k] ??= { total: 0, active: 0, completed: 0 };
        counts[k].total++;
        if (p.status === "active") counts[k].active++;
        if (p.status === "completed") counts[k].completed++;
      }
    }

    return {
      template: tmpl,
      versions: (versions ?? []).map((v: any) => ({
        ...v,
        projects: counts[v.id] ?? { total: 0, active: 0, completed: 0 },
      })),
      log: log ?? [],
    };
  });

export const ensureDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await loadAdmin();
    const { data: id, error } = await admin.rpc("ensure_template_draft", {
      _template_id: data.templateId,
      _actor_id: context.userId,
    });
    if (error) throw new Error(error.message);
    return { draftVersionId: id as unknown as string };
  });

export const publishDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string; summary?: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await loadAdmin();

    // Captura o current_version_id ANTES de publicar (vira o base do diff).
    const { data: tplBefore } = await admin
      .from("journey_templates")
      .select("current_version_id")
      .eq("id", data.templateId)
      .maybeSingle();
    const baseVersionId: string | null = tplBefore?.current_version_id ?? null;

    const { data: id, error } = await admin.rpc("publish_template_draft", {
      _template_id: data.templateId,
      _actor_id: context.userId,
      _summary: (data.summary ?? null) as never,
    });
    if (error) throw new Error(error.message);

    // Computa e persiste o diff no log de auditoria (best-effort).
    try {
      const diff = await computeDetailedDiff(admin, baseVersionId, id as unknown as string, data.templateId);
      await admin.from("journey_publication_log")
        .update({ diff, base_version_id: baseVersionId })
        .eq("template_id", data.templateId)
        .eq("version_id", id as unknown as string)
        .eq("action", "publish");
    } catch (e) {
      // Não interrompe a publicação em caso de falha do diff.
      console.error("[publishDraft] persist diff failed:", e);
    }

    return { versionId: id as unknown as string };
  });

export const restoreVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string; sourceVersionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await loadAdmin();
    const { data: id, error } = await admin.rpc("restore_template_version", {
      _template_id: data.templateId,
      _source_version_id: data.sourceVersionId,
      _actor_id: context.userId,
    });
    if (error) throw new Error(error.message);
    return { draftVersionId: id as unknown as string };
  });

export const archiveVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { versionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    // Guard: never archive a version that is currently pinned to active projects
    const { count } = await s
      .from("user_projects")
      .select("id", { count: "exact", head: true })
      .eq("template_version_id", data.versionId)
      .eq("status", "active");
    if ((count ?? 0) > 0) {
      throw new Error(`Não é possível arquivar: ${count} projeto(s) ativo(s) ainda usam esta versão.`);
    }
    const { error } = await s
      .from("journey_versions")
      .update({ status: "archived" })
      .eq("id", data.versionId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getVersionSnapshot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { versionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { data: v, error } = await context.supabase
      .from("journey_versions")
      .select("id, template_id, version_number, status, snapshot, change_summary, published_at, published_by, created_at")
      .eq("id", data.versionId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return v;
  });

export const getProjectsUsingVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { versionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await loadAdmin();
    const { data: projects } = await admin
      .from("user_projects")
      .select("id, user_id, status, progress_pct, created_at, last_activity_at")
      .eq("template_version_id", data.versionId)
      .order("created_at", { ascending: false });
    return { projects: projects ?? [] };
  });

export const migrateProjectsToVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { projectIds: string[]; targetVersionId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    if (data.projectIds.length === 0) return { migrated: 0 };
    const admin = await loadAdmin();
    // Confirm target version exists & is published
    const { data: v } = await admin
      .from("journey_versions")
      .select("id, status, template_id")
      .eq("id", data.targetVersionId)
      .maybeSingle();
    if (!v || v.status !== "published") {
      throw new Error("Versão de destino inválida ou não publicada.");
    }
    const { error } = await admin
      .from("user_projects")
      .update({ template_version_id: data.targetVersionId })
      .in("id", data.projectIds);
    if (error) throw new Error(error.message);
    return { migrated: data.projectIds.length };
  });

// ---------- Diff estrutural e item-a-item entre duas versões ----------
// Comparação: baseVersionId → targetVersionId (ambos são versões existentes;
// para "rascunho atual" usar o id da versão draft). Se baseVersionId=null,
// usa a versão publicada atual do template como base.
function indexStages(snap: any) {
  const stages = (snap?.stages ?? []) as any[];
  const map = new Map<string, any>();
  for (const s of stages) map.set(s.id, s);
  return { stages, map };
}
function indexMissions(snap: any) {
  const map = new Map<string, { m: any; stageId: string; stageTitle: string; stageOrder: number }>();
  for (const s of (snap?.stages ?? []) as any[]) {
    for (const m of s.missions ?? []) {
      map.set(m.id, { m, stageId: s.id, stageTitle: s.title, stageOrder: s.order_index });
    }
  }
  return map;
}

const STAGE_TEXT_FIELDS = ["title", "description"] as const;
const MISSION_TEXT_FIELDS = ["title", "objective", "description", "difficulty", "estimated_minutes", "depends_on_mission_id"] as const;

function fieldDiff(before: any, after: any, fields: readonly string[]) {
  const changes: Array<{ field: string; from: any; to: any }> = [];
  for (const f of fields) {
    const a = before?.[f] ?? null;
    const b = after?.[f] ?? null;
    if (JSON.stringify(a) !== JSON.stringify(b)) changes.push({ field: f, from: a, to: b });
  }
  return changes;
}

function buildDetailedDiff(baseSnap: any, targetSnap: any) {
  const baseIdx = indexStages(baseSnap);
  const tgtIdx = indexStages(targetSnap);
  const baseMissions = indexMissions(baseSnap);
  const tgtMissions = indexMissions(targetSnap);

  const stages = {
    added: [] as any[], removed: [] as any[], reordered: [] as any[], modified: [] as any[],
  };
  const missions = {
    added: [] as any[], removed: [] as any[], moved: [] as any[], modified: [] as any[],
  };
  const slidesByMission: any[] = [];
  const questionsByMission: any[] = [];
  const deliverablesByMission: any[] = [];
  const externalsByMission: any[] = [];
  const resourcesByMission: any[] = [];
  const incompatible: any[] = [];

  // Stages
  for (const s of tgtIdx.stages) {
    const b = baseIdx.map.get(s.id);
    if (!b) { stages.added.push({ id: s.id, title: s.title, order_index: s.order_index }); continue; }
    if (b.order_index !== s.order_index) stages.reordered.push({ id: s.id, title: s.title, from: b.order_index, to: s.order_index });
    const changes = fieldDiff(b, s, STAGE_TEXT_FIELDS);
    if (changes.length) stages.modified.push({ id: s.id, title: s.title, changes });
  }
  for (const s of baseIdx.stages) if (!tgtIdx.map.has(s.id)) stages.removed.push({ id: s.id, title: s.title });

  // Missions
  for (const [id, entry] of tgtMissions) {
    const before = baseMissions.get(id);
    if (!before) {
      missions.added.push({ id, title: entry.m.title, stage_id: entry.stageId, stage_title: entry.stageTitle });
      continue;
    }
    if (before.stageId !== entry.stageId) {
      missions.moved.push({ id, title: entry.m.title, from_stage: before.stageTitle, to_stage: entry.stageTitle });
    }
    const changes = fieldDiff(before.m, entry.m, MISSION_TEXT_FIELDS);
    if (changes.length) missions.modified.push({ id, title: entry.m.title, changes });

    // Slides
    const bs = (before.m.slides ?? []) as any[];
    const ts = (entry.m.slides ?? []) as any[];
    const bsIds = new Map(bs.map((x) => [x.id, x]));
    const tsIds = new Map(ts.map((x) => [x.id, x]));
    let sAdded = 0, sRemoved = 0, sModified = 0;
    for (const t of ts) {
      const b2 = bsIds.get(t.id);
      if (!b2) sAdded++;
      else if (JSON.stringify(b2) !== JSON.stringify(t)) sModified++;
    }
    for (const b2 of bs) if (!tsIds.has(b2.id)) sRemoved++;
    if (sAdded || sRemoved || sModified) {
      slidesByMission.push({ mission_id: id, mission_title: entry.m.title, added: sAdded, removed: sRemoved, modified: sModified });
    }

    // Questions — por key (chave estável para respostas)
    const bq = (before.m.questions ?? []) as any[];
    const tq = (entry.m.questions ?? []) as any[];
    const bqByKey = new Map(bq.map((x) => [x.key, x]));
    const tqByKey = new Map(tq.map((x) => [x.key, x]));
    const qAdded: any[] = [], qRemoved: any[] = [], qModified: any[] = [];
    for (const t of tq) {
      const b2 = bqByKey.get(t.key);
      if (!b2) { qAdded.push({ key: t.key, input_type: t.input_type, label: t.label }); continue; }
      const ch = fieldDiff(b2, t, ["label", "input_type", "options", "required", "help_text"]);
      if (ch.length) qModified.push({ key: t.key, changes: ch });
      if (b2.input_type !== t.input_type) {
        incompatible.push({ type: "question_type_changed", mission_id: id, mission_title: entry.m.title, question_key: t.key, from: b2.input_type, to: t.input_type });
      }
    }
    for (const b2 of bq) {
      if (!tqByKey.has(b2.key)) {
        qRemoved.push({ key: b2.key, label: b2.label });
        incompatible.push({ type: "question_removed", mission_id: id, mission_title: entry.m.title, question_key: b2.key });
      }
    }
    if (qAdded.length || qRemoved.length || qModified.length) {
      questionsByMission.push({ mission_id: id, mission_title: entry.m.title, added: qAdded, removed: qRemoved, modified: qModified });
    }

    // Deliverable
    const bd = before.m.deliverable ?? null;
    const td = entry.m.deliverable ?? null;
    if (JSON.stringify(bd) !== JSON.stringify(td)) {
      const ch: any[] = [];
      if ((bd?.kind ?? null) !== (td?.kind ?? null)) ch.push({ field: "kind", from: bd?.kind ?? null, to: td?.kind ?? null });
      if ((bd?.target_field ?? null) !== (td?.target_field ?? null)) {
        ch.push({ field: "target_field", from: bd?.target_field ?? null, to: td?.target_field ?? null });
        if (bd && td) incompatible.push({ type: "deliverable_target_field_changed", mission_id: id, mission_title: entry.m.title, from: bd.target_field, to: td.target_field });
      }
      if ((bd?.title ?? null) !== (td?.title ?? null)) ch.push({ field: "title", from: bd?.title ?? null, to: td?.title ?? null });
      deliverablesByMission.push({ mission_id: id, mission_title: entry.m.title, present_before: !!bd, present_after: !!td, changes: ch });
    }

    // Externals
    const be = (before.m.externals ?? []) as any[];
    const te = (entry.m.externals ?? []) as any[];
    const beIds = new Map(be.map((x) => [x.id, x]));
    const teIds = new Map(te.map((x) => [x.id, x]));
    let eA = 0, eR = 0, eM = 0;
    for (const t of te) { const b2 = beIds.get(t.id); if (!b2) eA++; else if (JSON.stringify(b2) !== JSON.stringify(t)) eM++; }
    for (const b2 of be) if (!teIds.has(b2.id)) eR++;
    if (eA || eR || eM) externalsByMission.push({ mission_id: id, mission_title: entry.m.title, added: eA, removed: eR, modified: eM });

    // Resources
    const br = (before.m.resources ?? []) as any[];
    const tr = (entry.m.resources ?? []) as any[];
    const brIds = new Map(br.map((x) => [x.id, x]));
    const trIds = new Map(tr.map((x) => [x.id, x]));
    let rA = 0, rR = 0, rM = 0;
    for (const t of tr) { const b2 = brIds.get(t.id); if (!b2) rA++; else if (JSON.stringify(b2) !== JSON.stringify(t)) rM++; }
    for (const b2 of br) if (!trIds.has(b2.id)) rR++;
    if (rA || rR || rM) resourcesByMission.push({ mission_id: id, mission_title: entry.m.title, added: rA, removed: rR, modified: rM });
  }
  for (const [id, entry] of baseMissions) {
    if (!tgtMissions.has(id)) {
      missions.removed.push({ id, title: entry.m.title, stage_title: entry.stageTitle });
      for (const [, other] of tgtMissions) {
        if (other.m.depends_on_mission_id === id) {
          incompatible.push({ type: "dependency_broken", mission_id: other.m.id, mission_title: other.m.title, missing_dep: id });
        }
      }
    }
  }

  return {
    structural: {
      stages_added: stages.added.length,
      stages_removed: stages.removed.length,
      stages_reordered: stages.reordered.length,
      missions_added: missions.added.length,
      missions_removed: missions.removed.length,
    },
    content: {
      slides_changed: slidesByMission.length,
      questions_changed: questionsByMission.length,
      deliverables_changed: deliverablesByMission.length,
    },
    incompatible,
    items: {
      stages, missions,
      slides: slidesByMission,
      questions: questionsByMission,
      deliverables: deliverablesByMission,
      externals: externalsByMission,
      resources: resourcesByMission,
    },
  };
}

async function computeDetailedDiff(
  s: any, baseVersionId: string | null, targetVersionId: string, templateId: string,
) {
  const { data: target } = await s
    .from("journey_versions").select("snapshot, version_number, status")
    .eq("id", targetVersionId).maybeSingle();
  if (!target) throw new Error("Versão alvo não encontrada.");

  let baseSnap: any = null;
  let baseMeta: any = null;
  let baseId = baseVersionId;
  if (!baseId) {
    const { data: t } = await s.from("journey_templates")
      .select("current_version_id").eq("id", templateId).maybeSingle();
    baseId = t?.current_version_id ?? null;
  }
  if (baseId) {
    const { data: b } = await s
      .from("journey_versions").select("snapshot, version_number, status")
      .eq("id", baseId).maybeSingle();
    baseSnap = b?.snapshot ?? null;
    baseMeta = b ? { id: baseId, version_number: b.version_number, status: b.status } : null;
  }

  const targetMeta = { id: targetVersionId, version_number: target.version_number, status: target.status };

  if (!baseSnap) {
    return {
      structural: { stages_added: (target.snapshot?.stages ?? []).length, stages_removed: 0, stages_reordered: 0, missions_added: 0, missions_removed: 0 },
      content: { slides_changed: 0, questions_changed: 0, deliverables_changed: 0 },
      incompatible: [],
      items: { stages: { added: [], removed: [], reordered: [], modified: [] }, missions: { added: [], removed: [], moved: [], modified: [] }, slides: [], questions: [], deliverables: [], externals: [], resources: [] },
      summary: "Primeira publicação — sem versão anterior para comparação.",
      base: null, target: targetMeta,
    };
  }

  const detailed = buildDetailedDiff(baseSnap, target.snapshot);
  return { ...detailed, base: baseMeta, target: targetMeta, summary: null };
}

export const diffVersions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { baseVersionId?: string | null; targetVersionId: string; templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return await computeDetailedDiff(context.supabase, data.baseVersionId ?? null, data.targetVersionId, data.templateId);
  });

// ---------- Auditoria (log de publicações) ----------
export const getPublicationAudit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const { data: log } = await s
      .from("journey_publication_log")
      .select("id, version_id, base_version_id, action, actor_id, notes, diff, created_at")
      .eq("template_id", data.templateId)
      .order("created_at", { ascending: false })
      .limit(100);
    const entries = log ?? [];
    const actorIds = Array.from(new Set(entries.map((e: any) => e.actor_id).filter(Boolean)));
    const versionIds = Array.from(new Set(entries.flatMap((e: any) => [e.version_id, e.base_version_id]).filter(Boolean)));

    const [{ data: actors }, { data: versions }] = await Promise.all([
      actorIds.length ? s.from("profiles").select("id, email, full_name").in("id", actorIds) : Promise.resolve({ data: [] as any[] }),
      versionIds.length ? s.from("journey_versions").select("id, version_number, status").in("id", versionIds) : Promise.resolve({ data: [] as any[] }),
    ]);
    const actorMap = new Map((actors ?? []).map((a: any) => [a.id, a]));
    const versionMap = new Map((versions ?? []).map((v: any) => [v.id, v]));

    return {
      entries: entries.map((e: any) => ({
        ...e,
        actor: e.actor_id ? actorMap.get(e.actor_id) ?? null : null,
        version: e.version_id ? versionMap.get(e.version_id) ?? null : null,
        base_version: e.base_version_id ? versionMap.get(e.base_version_id) ?? null : null,
      })),
    };
  });

// ============ Soft delete / restore genérico ============
const SOFT_TABLES = new Set([
  "journey_stages", "journey_missions", "mission_slides", "mission_questions",
  "mission_deliverables", "mission_external_tasks", "mission_resources_contextual",
]);

export const softDeleteEntity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { table: string; id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    if (!SOFT_TABLES.has(data.table)) throw new Error("Tabela inválida.");
    const { error } = await (context.supabase as any)
      .from(data.table)
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const restoreEntity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { table: string; id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    if (!SOFT_TABLES.has(data.table)) throw new Error("Tabela inválida.");
    const { error } = await (context.supabase as any)
      .from(data.table)
      .update({ deleted_at: null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


// ============================================================
// Onda 2.B — Arquivamento / Restauração / Duplicação de jornadas
// ============================================================

export const archiveTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    // Bloqueia arquivamento se houver projetos ativos ligados à publicação corrente
    const { data: tpl } = await s.from("journey_templates")
      .select("id, current_version_id").eq("id", data.templateId).maybeSingle();
    if (!tpl) throw new Error("Jornada não encontrada.");
    if (tpl.current_version_id) {
      const { count } = await s.from("user_projects")
        .select("id", { count: "exact", head: true })
        .eq("template_version_id", tpl.current_version_id)
        .eq("status", "active");
      if ((count ?? 0) > 0) {
        throw new Error(`Não é possível arquivar: ${count} projeto(s) ativo(s) ainda usam esta jornada.`);
      }
    }
    const { error } = await s.from("journey_templates")
      .update({ archived_at: new Date().toISOString(), published: false })
      .eq("id", data.templateId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const restoreTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("journey_templates")
      .update({ archived_at: null }).eq("id", data.templateId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const duplicateTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { sourceTemplateId: string; newTitle?: string; targetBusinessId?: string | null }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await loadAdmin();
    const { data: id, error } = await admin.rpc("duplicate_template", {
      _source_template_id: data.sourceTemplateId,
      _actor_id: context.userId,
      _new_title: (data.newTitle ?? null) as never,
      _target_business_id: (data.targetBusinessId ?? null) as never,
    });
    if (error) throw new Error(error.message);
    return { templateId: id as unknown as string };
  });

// Lista os itens arquivados de uma jornada para permitir restauração pontual.
export const listArchivedItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const { data: stages } = await s.from("journey_stages")
      .select("id, title, order_index, deleted_at")
      .eq("template_id", data.templateId)
      .not("deleted_at", "is", null)
      .order("deleted_at", { ascending: false });

    // Todas as missões (arquivadas ou não) sob quaisquer stages do template — precisamos
    // do stage_id para filtrar, então pegamos ids de stages (ativos + arquivados).
    const { data: allStages } = await s.from("journey_stages")
      .select("id, title").eq("template_id", data.templateId);
    const stageIds = (allStages ?? []).map((x: any) => x.id);
    const stageTitleById = new Map<string, string>();
    for (const st of allStages ?? []) stageTitleById.set(st.id, st.title);

    const { data: missions } = stageIds.length
      ? await s.from("journey_missions")
          .select("id, title, stage_id, order_index, deleted_at")
          .in("stage_id", stageIds)
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false })
      : { data: [] as any[] };

    return {
      stages: stages ?? [],
      missions: (missions ?? []).map((m: any) => ({ ...m, stage_title: stageTitleById.get(m.stage_id) ?? null })),
    };
  });

// Restauração explícita para os wrappers da UI (compatível com softDeleteEntity/restoreEntity).
export const restoreStage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("journey_stages")
      .update({ deleted_at: null }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const restoreMission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("journey_missions")
      .update({ deleted_at: null }).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============================================================================
// SUB-LOTE 2.E — MÉTRICAS DE JORNADA
// Derivadas 100% de tabelas existentes (user_projects, user_mission_progress,
// user_task_progress, user_mission_state, mission_questions). Sem migrations.
// ============================================================================

export const getJourneyMetrics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { templateId: string; windowDays?: number }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const s = context.supabase;
    const { templateId } = data;
    const windowDays = data.windowDays ?? 30;
    const sinceISO = new Date(Date.now() - windowDays * 86400_000).toISOString();

    // 1) Estrutura da jornada (rascunho vivo — visão do editor)
    const { data: stages } = await s
      .from("journey_stages")
      .select("id, title, position")
      .eq("template_id", templateId)
      .is("deleted_at", null)
      .order("position");
    const stageIds = (stages ?? []).map((x: any) => x.id);
    const stageById = new Map<string, any>((stages ?? []).map((x: any) => [x.id, x]));

    const { data: missions } = stageIds.length
      ? await s.from("journey_missions")
          .select("id, stage_id, title, position")
          .in("stage_id", stageIds)
          .is("deleted_at", null)
          .order("position")
      : { data: [] as any[] };
    const missionIds = (missions ?? []).map((x: any) => x.id);
    const missionById = new Map<string, any>((missions ?? []).map((x: any) => [x.id, x]));

    const { data: questions } = missionIds.length
      ? await s.from("mission_questions")
          .select("id, mission_id, required")
          .in("mission_id", missionIds)
      : { data: [] as any[] };
    const questionsByMission = new Map<string, any[]>();
    for (const q of (questions ?? []) as any[]) {
      const arr = questionsByMission.get(q.mission_id) ?? [];
      arr.push(q);
      questionsByMission.set(q.mission_id, arr);
    }

    // 2) Projetos do template
    const { data: projects } = await s
      .from("user_projects")
      .select("id, status, started_at, completed_at, last_activity_at, progress_pct, archived_at")
      .eq("template_id", templateId);
    const projectIds = (projects ?? []).map((p: any) => p.id);

    const totalProjects = projects?.length ?? 0;
    const activeProjects = (projects ?? []).filter((p: any) => p.status === "active" && !p.archived_at).length;
    const completedProjects = (projects ?? []).filter((p: any) => p.status === "completed" || p.completed_at).length;
    const archivedProjects = (projects ?? []).filter((p: any) => !!p.archived_at).length;
    const activeRecent = (projects ?? []).filter((p: any) => p.last_activity_at && p.last_activity_at >= sinceISO).length;

    const durations = (projects ?? [])
      .filter((p: any) => p.completed_at && p.started_at)
      .map((p: any) => (new Date(p.completed_at).getTime() - new Date(p.started_at).getTime()) / 86400_000);
    const avgDaysToComplete = durations.length
      ? Number((durations.reduce((a: number, b: number) => a + b, 0) / durations.length).toFixed(1))
      : null;
    const completionRate = totalProjects ? completedProjects / totalProjects : 0;

    // 3) Progresso de missões
    const { data: mprog } = projectIds.length && missionIds.length
      ? await s.from("user_mission_progress")
          .select("mission_id, user_project_id, progress_pct, completed_at, created_at, updated_at")
          .in("user_project_id", projectIds)
          .in("mission_id", missionIds)
      : { data: [] as any[] };

    type MMetric = {
      missionId: string; stageId: string; missionTitle: string; stageTitle: string;
      started: number; completed: number; abandoned: number;
      completionRate: number; avgDays: number | null;
    };
    const perMission = new Map<string, MMetric>();
    for (const m of missions ?? []) {
      perMission.set(m.id, {
        missionId: m.id, stageId: m.stage_id,
        missionTitle: m.title, stageTitle: stageById.get(m.stage_id)?.title ?? "",
        started: 0, completed: 0, abandoned: 0, completionRate: 0, avgDays: null,
      });
    }
    const missionDurations = new Map<string, number[]>();
    for (const r of (mprog ?? []) as any[]) {
      const cell = perMission.get(r.mission_id);
      if (!cell) continue;
      cell.started++;
      if (r.completed_at) {
        cell.completed++;
        const d = (new Date(r.completed_at).getTime() - new Date(r.created_at).getTime()) / 86400_000;
        const arr = missionDurations.get(r.mission_id) ?? [];
        arr.push(d);
        missionDurations.set(r.mission_id, arr);
      } else {
        cell.abandoned++;
      }
    }
    for (const cell of perMission.values()) {
      cell.completionRate = cell.started ? cell.completed / cell.started : 0;
      const ds = missionDurations.get(cell.missionId);
      cell.avgDays = ds && ds.length ? Number((ds.reduce((a, b) => a + b, 0) / ds.length).toFixed(1)) : null;
    }
    const missionsArr = Array.from(perMission.values());

    // 4) Ponto de abandono: missão com maior nº absoluto de "iniciada e não concluída"
    const abandonmentPoint = missionsArr
      .filter((m) => m.abandoned > 0)
      .sort((a, b) => b.abandoned - a.abandoned)[0] ?? null;

    // Ranking de gargalos (top 5 por taxa de abandono, mínimo 3 iniciadas)
    const bottlenecks = missionsArr
      .filter((m) => m.started >= 3)
      .map((m) => ({ ...m, abandonmentRate: m.started ? m.abandoned / m.started : 0 }))
      .sort((a, b) => b.abandonmentRate - a.abandonmentRate)
      .slice(0, 5);

    // 5) Perguntas em branco: mission_state.answers vs mission_questions.required
    const { data: mstate } = projectIds.length && missionIds.length
      ? await s.from("user_mission_state")
          .select("mission_id, answers, finalized_at")
          .in("project_id", projectIds)
          .in("mission_id", missionIds)
      : { data: [] as any[] };

    let blankRequiredTotal = 0;
    let requiredAnsweredTotal = 0;
    const blankByMission = new Map<string, { blanks: number; total: number }>();
    for (const st of (mstate ?? []) as any[]) {
      const qs = questionsByMission.get(st.mission_id) ?? [];
      const requiredQs = qs.filter((q) => q.required);
      if (!requiredQs.length) continue;
      const ans = (st.answers && typeof st.answers === "object") ? st.answers : {};
      let blanks = 0;
      for (const q of requiredQs) {
        const v = (ans as any)[q.id];
        const isBlank = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
        if (isBlank) blanks++;
        else requiredAnsweredTotal++;
      }
      blankRequiredTotal += blanks;
      const cell = blankByMission.get(st.mission_id) ?? { blanks: 0, total: 0 };
      cell.blanks += blanks;
      cell.total += requiredQs.length;
      blankByMission.set(st.mission_id, cell);
    }
    const blankQuestionsRate = (blankRequiredTotal + requiredAnsweredTotal) > 0
      ? blankRequiredTotal / (blankRequiredTotal + requiredAnsweredTotal)
      : 0;

    // Missões com mais campos obrigatórios ignorados
    const topBlanks = Array.from(blankByMission.entries())
      .map(([mid, v]) => ({
        missionId: mid,
        missionTitle: missionById.get(mid)?.title ?? "—",
        blanks: v.blanks, total: v.total,
        rate: v.total ? v.blanks / v.total : 0,
      }))
      .filter((x) => x.blanks > 0)
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 5);

    return {
      windowDays,
      totals: {
        projects: totalProjects,
        active: activeProjects,
        completed: completedProjects,
        archived: archivedProjects,
        activeRecent,
      },
      completionRate,
      avgDaysToComplete,
      abandonmentPoint,
      blankQuestionsRate,
      missions: missionsArr,
      bottlenecks,
      topBlanks,
      structure: {
        stages: stages ?? [],
        missions: missions ?? [],
      },
    };
  });
